/**
 * プロセスモデルの階層表示と検索。
 *
 * モデル id は `<group>/<model>`（グループは入れ子可）なので、階層はパスから
 * 導ける。グループの表示名は API が `parent_groups` で返すのでそれを使い、
 * 取れないときは id をそのまま出す（表示名が無いより id が見えた方が探せる）。
 */

/** グループ階層の 1 段（API の parent_groups 由来）。 */
export type GroupSegment = {
  readonly id: string;
  readonly displayName: string;
};

/** 階層表示・検索に必要な最小の形。呼び出し側の型はこれを満たせばよい。 */
export type GroupableModel = {
  readonly processModelId: string;
  readonly displayName: string;
  readonly description?: string | null;
  readonly groupPath?: readonly GroupSegment[];
};

export type ModelGroup<T extends GroupableModel> = {
  /** グループのパス（`hr/internal`）。グループ無しは空文字。 */
  readonly path: string;
  /** 画面に出す名前。 */
  readonly label: string;
  readonly models: readonly T[];
};

/** モデル id からグループのパスを取る（最後の要素がモデル名）。 */
function groupPathOf(processModelId: string): string {
  const segments = processModelId.split("/");
  return segments.slice(0, -1).join("/");
}

/**
 * グループの表示名。API から取れればそれを繋ぎ、無ければ id を繋ぐ。
 * 入れ子は「親 / 子」の形にして階層が読めるようにする。
 */
function labelFor(path: string, groupPath?: readonly GroupSegment[]): string {
  if (path === "") return "";

  const names =
    groupPath !== undefined && groupPath.length > 0
      ? groupPath.map((segment) => segment.displayName)
      : path.split("/");

  return names.join(" / ");
}

export function groupModelsByPath<T extends GroupableModel>(
  models: readonly T[],
): readonly ModelGroup<T>[] {
  const byPath = new Map<string, { label: string; models: T[] }>();

  for (const model of models) {
    const path = groupPathOf(model.processModelId);
    const existing = byPath.get(path);
    if (existing === undefined) {
      byPath.set(path, {
        label: labelFor(path, model.groupPath),
        models: [model],
      });
    } else {
      existing.models.push(model);
    }
  }

  return [...byPath.entries()]
    .map(([path, entry]) => ({
      path,
      label: entry.label,
      models: [...entry.models].sort((a, b) =>
        a.displayName.localeCompare(b.displayName, "ja"),
      ),
    }))
    .sort((a, b) => a.path.localeCompare(b.path, "ja"));
}

/**
 * モデルを検索する。表示名・モデル id・グループ名・説明のいずれかに当たれば残す。
 *
 * id も対象にするのは、日本語表示名しか出ていないモデルを英字で探せるようにするため。
 * グループ名を対象にするのは、そのグループのモデルを一望する使い方があるため。
 */
export function searchModels<T extends GroupableModel>(
  models: readonly T[],
  keyword: string,
): readonly T[] {
  const needle = keyword.trim().toLowerCase();
  if (needle === "") return models;

  return models.filter((model) => {
    const haystack = [
      model.displayName,
      model.processModelId,
      model.description ?? "",
      ...(model.groupPath ?? []).map((segment) => segment.displayName),
    ]
      .join("\n")
      .toLowerCase();

    return haystack.includes(needle);
  });
}

/** 左の一覧に「件数 0」で出すグループ。 */
export type EmptyGroup = {
  readonly id: string;
  /** 親から繋いだ表示名（`人事 / 社内`）。見出しの `label` とそろえる。 */
  readonly label: string;
};

/**
 * 配下にモデルが 1 つも無いグループ。
 *
 * モデル一覧から組み立てる階層（groupModelsByPath）には、モデルの無いグループが
 * 出てこない。削除の導線を置くために、グループ一覧と突き合わせて拾う。
 *
 * 入れ子の子グループにモデルがあれば親は空とみなさない。backend のグループ削除は
 * 配下ごと消す（rmtree）ので、ここで空と判定したものだけを消させる。
 */
export function findEmptyGroups(
  groups: readonly GroupSegment[],
  modelIds: readonly string[],
): readonly EmptyGroup[] {
  const names = new Map(groups.map((group) => [group.id, group.displayName]));

  return groups
    .filter(
      (group) =>
        !modelIds.some((modelId) => modelId.startsWith(`${group.id}/`)),
    )
    .map((group) => {
      const segments = group.id.split("/");
      const label = segments
        .map((segment, index) => {
          const id = segments.slice(0, index + 1).join("/");
          return names.get(id) ?? segment;
        })
        .join(" / ");
      return { id: group.id, label };
    });
}
